import { useQuery } from '@tanstack/react-query';
import { useEffect, type ReactElement } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';

import { ApiError, violationMessage } from '../../lib/api';
import { useAuth } from '../../lib/auth';
// import { ApplicationDocumentsPanel } from '../components/ApplicationDocumentsPanel';
import { LearnerPrimaryAction } from '../components/LearnerPrimaryAction';
import { ParentShell } from '../components/ParentShell';
import { PaymentTable } from '../components/PaymentRow';
import { RejectedSubjectsNotes } from '../components/RejectedSubjectsNotes';
import {
  isRejectedSubject,
  subjectOutcomeBadgeClass,
  subjectOutcomeLabel,
} from '../lib/subject-outcome';
import { StatusBadge } from '../components/StatusBadge';
import { TimelineList } from '../components/TimelineList';
import {
  fetchLearnerDetail,
  fetchMyPayments,
  learnerNavFromSummary,
} from '../lib/parent-portal-api';
import { fetchMyLearners } from '../lib/registration-api';
import { setLastLearnerId } from '../lib/selected-learner';

const ALL_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'subjects', label: 'Subjects' },
  // Document-upload module parked.
  // { id: 'documents', label: 'Documents' },
  { id: 'payments', label: 'Payments' },
  { id: 'history', label: 'History' },
  { id: 'notes', label: 'Notes' },
] as const;

type TabId = (typeof ALL_TABS)[number]['id'];

function learnerInitials(first: string, last: string): string {
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

export function ChildProfilePage(): ReactElement {
  const { learnerId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') as TabId | null) ?? 'overview';
  const { token, loading } = useAuth();

  useEffect(() => {
    if (learnerId) setLastLearnerId(learnerId);
  }, [learnerId]);

  const learnersQuery = useQuery({
    queryKey: ['my-learners'],
    queryFn: fetchMyLearners,
    enabled: Boolean(token),
  });

  const detailQuery = useQuery({
    queryKey: ['learner-detail', learnerId],
    queryFn: () => fetchLearnerDetail(learnerId),
    enabled: Boolean(token && learnerId),
  });

  const paymentsQuery = useQuery({
    queryKey: ['my-payments', learnerId],
    queryFn: () => fetchMyPayments({ learnerId }),
    enabled: Boolean(token && learnerId && tab === 'payments'),
  });

  if (!loading && !token) return <Navigate to="/login" replace />;

  const detail = detailQuery.data;
  const learners = learnerNavFromSummary(learnersQuery.data ?? []);
  // Document-upload module parked.
  // const documentsRequired = detail?.application.documentsPolicy !== 'OPTIONAL';
  // const TABS = ALL_TABS.filter((item) => item.id !== 'documents' || documentsRequired);
  const TABS = ALL_TABS;

  function setTab(next: TabId): void {
    setSearchParams({ tab: next });
  }

  return (
    <ParentShell
      learners={learners}
      title="Child profile"
      headerActions={
        detail ? (
          <LearnerPrimaryAction
            applicationId={detail.application.id}
            applicationStatus={detail.application.status}
            academicBagId={detail.application.academicBagId}
            documentsPolicy={detail.application.documentsPolicy}
          />
        ) : null
      }
    >
      {detailQuery.error instanceof ApiError && detailQuery.error.problem.status === 404 ? (
        <div className="empty-state">
          <p className="h2">Child not found</p>
          <Link className="btn btn-secondary" to="/register">Register a child</Link>
        </div>
      ) : null}

      {detailQuery.error && !(detailQuery.error instanceof ApiError && detailQuery.error.problem.status === 404) ? (
        <div className="error-banner" role="alert">
          {detailQuery.error instanceof ApiError
            ? violationMessage(detailQuery.error.problem)
            : 'Could not load profile'}
        </div>
      ) : null}

      {detailQuery.isLoading ? <p className="text-muted">Loading…</p> : null}

      {detail ? (
        <>
          <div className="profile-head">
            <span className="profile-avatar" aria-hidden="true">
              {learnerInitials(detail.learner.firstName, detail.learner.lastName)}
            </span>
            <div className="profile-head-text">
              <h2 className="h1">
                {detail.learner.firstName} {detail.learner.middleName ? `${detail.learner.middleName} ` : ''}
                {detail.learner.lastName}
              </h2>
              <p className="profile-enrolment">
                {detail.learner.enrolmentNumber}
                {detail.academicLevel ? ` · ${detail.academicLevel.name}` : ''}
                {detail.school?.name ? ` · ${detail.school.name}` : ''}
              </p>
            </div>
            <div className="profile-head-actions">
              <StatusBadge status={detail.application.status} />
            </div>
          </div>

          <nav className="tabs" aria-label="Child profile sections">
            {TABS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`tab${tab === item.id ? ' is-active' : ''}`}
                aria-current={tab === item.id ? 'page' : undefined}
                onClick={() => setTab(item.id)}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {tab === 'overview' ? (
            <div className="stack-lg">
              <dl className="detail-grid">
                <div><dt>Date of birth</dt><dd>{detail.learner.dateOfBirth}</dd></div>
                <div><dt>City</dt><dd>{detail.learner.city}, {detail.learner.state}</dd></div>
                <div><dt>Application</dt><dd>{detail.application.applicationNumber}</dd></div>
                <div><dt>Source</dt><dd>{detail.learner.applicantSourceType.replaceAll('_', ' ').toLowerCase()}</dd></div>
                {detail.learner.schoolAdmissionNumber ? (
                  <div><dt>School admission number</dt><dd>{detail.learner.schoolAdmissionNumber}</dd></div>
                ) : null}
              </dl>
              <section>
                <h3 className="h3">Recent activity</h3>
                <TimelineList events={detail.timeline} limit={5} />
              </section>
            </div>
          ) : null}

          {tab === 'subjects' ? (
            detail.subjects.length === 0 ? (
              <p className="text-muted">No subjects selected yet.</p>
            ) : (
              <ul className="review-subject-list">
                {detail.subjects.map((subject) => (
                  <li
                    key={subject.id}
                    className={`review-subject-row${isRejectedSubject(subject) ? ' is-rejected' : ''}`}
                  >
                    <div className="review-subject-main">
                      <div>
                        <strong>{subject.subjectName}</strong>
                        {subject.syllabusCode ? (
                          <span className="subject-syllabus-code">{subject.syllabusCode}</span>
                        ) : null}
                      </div>
                    </div>
                    <span className={subjectOutcomeBadgeClass(subject)}>
                      {subjectOutcomeLabel(subject, {
                        applicationStatus: detail.application.status,
                        bagStatus: detail.application.academicBagStatus,
                      })}
                    </span>
                  </li>
                ))}
              </ul>
            )
          ) : null}

          {/* Document-upload module parked.
          {tab === 'documents' ? (
            <ApplicationDocumentsPanel applicationId={detail.application.id} />
          ) : null}
          */}

          {tab === 'payments' ? (
            paymentsQuery.isLoading ? (
              <p className="text-muted">Loading payments…</p>
            ) : (
              <PaymentTable items={paymentsQuery.data?.items ?? []} />
            )
          ) : null}

          {tab === 'history' ? <TimelineList events={detail.timeline} /> : null}

          {tab === 'notes' ? (
            <RejectedSubjectsNotes subjects={detail.subjects} showWhenEmpty standalone />
          ) : null}
        </>
      ) : null}
    </ParentShell>
  );
}
